import RiShoppingBasketLine from '@ben/icons/shopping-basket-line';
import RiWallet3Line from '@ben/icons/wallet-3-line';
import IconTile from '@ben/orion/icon-tile/icon-tile';
import type { RIconComponent } from '@ben/orion/remix-icon';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

export type Action = 'buy' | 'sell';

interface Option {
  icon: RIconComponent;
  label: string;
  value: Action;
}

interface SelectionButtonGroupProps {
  value?: Action;
  onChange: (value: Action) => void;
  name?: string;
  disabled?: boolean;
  isBuyOnly?: boolean;
  isSellOnly?: boolean;
}

const SelectionButtonGroup = ({
  value,
  onChange,
  name: providedName,
  disabled,
  isBuyOnly = false,
  isSellOnly = false,
}: SelectionButtonGroupProps) => {
  const groupId = useId();
  const name = providedName ?? `radio-group-${groupId}`;
  const { t } = useTranslation();

  const options: Option[] = useMemo(() => {
    const baseOptions: Option[] = [
      {
        icon: RiShoppingBasketLine,
        label: t('words.buy'),
        value: 'buy',
      },
      {
        icon: RiWallet3Line,
        label: t('words.sell'),
        value: 'sell',
      },
    ];
    if (isBuyOnly)
      return baseOptions.filter((option) => option.value === 'buy');
    if (isSellOnly)
      return baseOptions.filter((option) => option.value === 'sell');
    return baseOptions;
  }, [t, isBuyOnly, isSellOnly]);

  return (
    <RadioGroup role="radiogroup" aria-label={name}>
      {options.map((option) => (
        <IconTile
          key={option.value}
          label={option.label}
          icon={option.icon}
          isSelected={value === option.value}
          onClick={() => {
            if (value !== option.value) onChange(option.value);
          }}
          role="radio"
          disabled={disabled}
        />
      ))}
    </RadioGroup>
  );
};

export default SelectionButtonGroup;

const RadioGroup = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spacing(2)};
  `}
`;
